import { type Component, For, onCleanup, onMount, Show } from "solid-js"
import { type OfferingProps } from "../shared/offering-props"
import WebinarPurchaseButton from "./WebinarPurchaseButton"
import ProfitTreeIcon from "../../assets/images/profittree-trees-icon.svg"
import RupertAi from "../../assets/images/rupert-ai.png"

const rupertTileRadialGlows =
  "radial-gradient(circle at 90% 19%, rgba(253,29,235,0.3) 0%, rgba(253,29,235,0) 55%)," +
  "radial-gradient(circle at 26% 26%, rgba(84,255,65,0.3) 0%, rgba(84,255,65,0) 55%)," +
  "radial-gradient(circle at 80% 68%, rgba(59,130,246,0.3) 0%, rgba(59,130,246,0) 100%)," +
  "radial-gradient(circle at 23% 36%, rgba(52,199,207,0.27) 0%, rgba(96,223,229,0) 75%)"

const BONUSES = [
  {
    label: "Bonus 1",
    value: "$697 value",
    title: "Etsy Starter Course",
    description: "Launch your first bestseller without guessing what sells.",
  },
  {
    label: "Bonus 2",
    value: "$997 value",
    title: "Etsy Ads Master Course",
    description: "Turn Etsy Ads into profit, not wasted clicks.",
  },
  {
    label: "Bonus 3",
    value: "$97 value",
    title: "Etsy Pricing Calculator",
    description: "Know your real profit before you set a price.",
  },
]

const formatUsd = (value?: number, numFractionDigits: number = 2): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    minimumFractionDigits: numFractionDigits,
  }).format(value!)
}

const OfferIncludes: Component = () => (
  <div class='flex flex-col gap-4 w-full'>
    <p class='text-2xl leading-8 font-bold font-poppins text-white text-center'>
      The Offer Includes
    </p>

    <div class='flex flex-col sm:flex-row gap-4 items-stretch justify-center w-full'>
      <div class='flex-1 flex flex-col gap-4 items-start bg-white border border-gray-200 rounded-lg p-4 text-start'>
        <div class='flex items-start justify-between w-full'>
          <div class='relative shrink-0 size-14 rounded-lg bg-gunmetal overflow-hidden flex justify-center'>
            <img src={ProfitTreeIcon} alt='' class='w-[22.68px] h-[30.35px] mt-[5px]' />
            <div class='absolute inset-x-0 bottom-0 h-[17px] bg-[#FF0004] flex items-center justify-center'>
              <span class='font-bold text-white text-[9px] leading-4'>PRO</span>
            </div>
          </div>
          <span class='shrink-0 bg-amber-100 text-amber-900 font-bold text-xs leading-4 rounded-sm px-2 py-0.5'>
            BEST PRICE ON PRO
          </span>
        </div>
        <div class='flex flex-col gap-1 w-full'>
          <p class='text-lg leading-7 font-bold text-black'>ProfitTree Pro Annual Plan</p>
          <p class='text-sm leading-5 font-normal text-gray-700'>
            Get the full potential of ProfitTree with your Pro account, every metric, every feature,
            all-time history. All you need to boost your Etsy sales.
          </p>
        </div>
      </div>

      <div class='flex-1 flex flex-col gap-4 items-start bg-white border border-gray-200 rounded-lg p-4 text-start'>
        <div class='flex items-start justify-between w-full'>
          <div
            class='relative shrink-0 size-14 rounded-lg bg-white overflow-hidden shadow-[0px_0px_16.32px_-2.04px_rgba(21,72,102,0.2)]'
            style={{ "background-image": rupertTileRadialGlows }}
          >
            <img
              src={RupertAi}
              alt=''
              class='absolute left-[7px] top-[6px] w-[44px] h-[58px] object-contain object-top'
            />
          </div>
          <span class='shrink-0 bg-amber-100 text-amber-900 font-bold text-xs leading-4 rounded-sm px-2 py-0.5'>
            WEBINAR EXCLUSIVE
          </span>
        </div>
        <div class='flex flex-col gap-1 w-full'>
          <p class='text-lg leading-7 font-bold text-black'>1 Year Unlimited AI Credits</p>
          <p class='text-sm leading-5 font-normal text-gray-700'>
            Research markets, validate ideas, draft listings, and track your shop. Pro includes
            4,000 credits a week. Yours are unlimited for 12 months.
          </p>
        </div>
      </div>
    </div>
  </div>
)

const FreeBonuses: Component = () => (
  <div class='flex flex-col gap-4 w-full'>
    <p class='text-2xl leading-8 font-bold font-poppins text-white text-center'>
      Plus 3 <span class='text-amber-400'>FREE</span> Bonuses! (Worth $1,791)
    </p>

    <div class='flex flex-col sm:flex-row gap-4 items-stretch justify-center w-full'>
      <For each={BONUSES}>
        {(bonus) => (
          <div class='flex-1 flex flex-col gap-1 items-start bg-white border border-gray-200 rounded-lg p-4 text-start'>
            <div class='flex gap-2 items-center w-full'>
              <span class='flex-1 font-extrabold uppercase text-xs leading-4 text-[#F57149]'>
                {bonus.label}
              </span>
              <span class='shrink-0 bg-amber-100 text-amber-900 font-bold uppercase text-xs leading-4 rounded-sm px-2 py-0.5 whitespace-nowrap'>
                {bonus.value}
              </span>
            </div>
            <p class='text-lg leading-7 font-bold text-black'>{bonus.title}</p>
            <p class='text-sm leading-5 font-normal text-gray-700'>{bonus.description}</p>
          </div>
        )}
      </For>
    </div>
  </div>
)

const PriceSummary: Component<{ offeringPrice?: number }> = (props) => (
  <div class='flex flex-col gap-2 w-full text-center'>
    <p class='text-3xl leading-9 font-bold font-poppins text-white'>
      Today's Price: <span class='text-forest_green-500'>{formatUsd(props.offeringPrice, 0)}</span>
    </p>
    <p class='text-sm leading-5 font-normal text-gray-300'>
      💰 Save <strong class='font-bold'>$299.88</strong> vs monthly pricing on Pro plus get{" "}
      <strong class='font-bold'>$1,791</strong> worth of extras!
    </p>
  </div>
)

const RenewalTerms: Component = () => (
  <div class='flex flex-col gap-2 font-sans text-xs leading-4 text-gray-50 text-center'>
    <span>Renews at $359.88 after one year. Cancel anytime.</span>
    <span>
      Unlimited credits apply for 12 months; Pro then returns to 4,000/week. Fair-use limits apply
      to automated usage.
    </span>
  </div>
)

const WebinarOfferingBox: Component<
  OfferingProps & { onCtaVisibilityChange?: (visible: boolean) => void }
> = (props) => {
  let ctaContainer: HTMLDivElement | undefined

  onMount(() => {
    if (!ctaContainer || !props.onCtaVisibilityChange) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        props.onCtaVisibilityChange?.(entry.isIntersecting)
      },
      { threshold: 1 }
    )
    observer.observe(ctaContainer)
    onCleanup(() => {
      observer.disconnect()
    })
  })

  return (
    <div class='w-full max-w-[800px] flex flex-col gap-8'>
      <OfferIncludes />
      <FreeBonuses />
      <div class='flex flex-col gap-6 w-full'>
        <PriceSummary offeringPrice={props.offeringPrice} />
        <Show when={props.showButton}>
          <div ref={ctaContainer}>
            <WebinarPurchaseButton showCheckout={props.showCheckout} fullWidth={true} />
          </div>
        </Show>

        <RenewalTerms />
      </div>
    </div>
  )
}

export default WebinarOfferingBox
